import { CompleteCareDialog } from "@/features/care-queue/components/CompleteCareDialog";
import { CareHistory } from "@/features/care-queue/components/CareHistory";
import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { canOpenPage } from "@/features/authorization/pageAccess";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Clock3,
  HeartPulse,
  Search,
  ShieldAlert,
  Sparkles,
} from "lucide-react";

import {
  getCareQueue,
  updateCareQueueItem,
  type CareQueueItem,
  type CareQueuePriority,
  type CareQueueStatus,
} from "@/api/careQueue";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingPage } from "@/components/ui/LoadingPage";
import { QueryErrorState } from "@/components/ui/QueryErrorState";
import { PageContainer } from "@/design-system";
import { useAuth } from "@/features/auth/hooks/useAuth";

type RiskFilter = "ALL" | CareQueuePriority;
type StatusFilter = "ALL" | CareQueueStatus;

export function CareQueuePage() {
  const { churchId } = useAuth();
  const queryClient = useQueryClient();

  const [completing, setCompleting] = useState<CareQueueItem | null>(null);
  const [search, setSearch] = useState("");
  const [riskFilter, setRiskFilter] = useState<RiskFilter>("ALL");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["care-queue", churchId],
    enabled: !!churchId,
    queryFn: () => getCareQueue(churchId!),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      itemId,
      status,
      completion_comment,
    }: {
      itemId: string;
      status: CareQueueStatus;
      completion_comment?: string;
    }) => updateCareQueueItem(churchId!, itemId, { status, completion_comment }),
    onSuccess: () => {
      for (const key of ["care-queue", "member-care-queue", "dashboard", "household"]) {
        void queryClient.invalidateQueries({ queryKey: [key, churchId] });
      }
    },
  });

  const items = useMemo(() => data?.items ?? [], [data]);

  const counts = useMemo(() => {
    return {
      all: items.length,
      critical: items.filter((item) => item.priority === "CRITICAL").length,
      high: items.filter((item) => item.priority === "HIGH").length,
      medium: items.filter((item) => item.priority === "MEDIUM").length,
      low: items.filter((item) => item.priority === "LOW").length,
      open: items.filter((item) => item.status === "OPEN").length,
      inProgress: items.filter((item) => item.status === "IN_PROGRESS").length,
      completed: items.filter((item) => item.status === "COMPLETED").length,
    };
  }, [items]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();

    return items.filter((item) => {
      const matchesSearch =
        !query ||
        item.title.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.recommended_action.toLowerCase().includes(query);

      const matchesRisk =
        riskFilter === "ALL" || item.priority === riskFilter;

      const matchesStatus =
        statusFilter === "ALL" || item.status === statusFilter;

      return matchesSearch && matchesRisk && matchesStatus;
    });
  }, [items, riskFilter, search, statusFilter]);

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingPage />
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer>
        <QueryErrorState
          title="Unable to load Care/Follow-up"
          description="MinistryIQ couldn't load your care queue. Check your connection and try again."
          onRetry={() => void refetch()}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {completing && <CompleteCareDialog item={completing} onClose={() => setCompleting(null)}
        onComplete={completion_comment => updateMutation.mutateAsync({ itemId: completing.id, status: "COMPLETED", completion_comment })} />}
      {updateMutation.error && !completing && <p role="alert" className="text-red-300">{updateMutation.error.message}</p>}
      <section className="rounded-3xl border border-slate-800 bg-gradient-to-r from-slate-900 via-slate-900 to-slate-950 p-7 shadow-sm shadow-black/20">
        <div className="flex flex-col gap-6 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-5">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500/30 to-violet-500/20 text-indigo-300 ring-1 ring-inset ring-indigo-400/20">
              <HeartPulse size={30} />
            </div>

            <div>
              <div className="flex items-center justify-between gap-3">
                <h1 className="text-3xl font-bold tracking-tight text-white">
                  Care/Follow-up
                </h1>

                {isFetching && (
                  <p
                    role="status"
                    className="text-xs font-medium text-indigo-300"
                  >
                    Refreshing care queue...
                  </p>
                )}
              </div>

              <p className="mt-2 text-base text-slate-400">
                Prioritized ministry follow-up requiring attention. Take action,
                track progress, and care well.
              </p>
            </div>
          </div>
         
        </div>
      </section>      

      <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap gap-2">
            <FilterButton
              active={riskFilter === "ALL"}
              label="All"
              count={counts.all}
              onClick={() => setRiskFilter("ALL")}
            />

            <FilterButton
              active={riskFilter === "CRITICAL"}
              label="Critical"
              count={counts.critical}
              tone="critical"
              onClick={() => setRiskFilter("CRITICAL")}
            />

            <FilterButton
              active={riskFilter === "HIGH"}
              label="At Risk"
              count={counts.high}
              tone="risk"
              onClick={() => setRiskFilter("HIGH")}
            />

            <FilterButton
              active={riskFilter === "MEDIUM"}
              label="Watch"
              count={counts.medium}
              tone="watch"
              onClick={() => setRiskFilter("MEDIUM")}
            />

            <FilterButton
              active={riskFilter === "LOW"}
              label="Healthy"
              count={counts.low}
              tone="healthy"
              onClick={() => setRiskFilter("LOW")}
            />
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value as StatusFilter)
              }
              className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-indigo-500"
            >
              <option value="ALL">All Statuses</option>
              <option value="OPEN">Needs Attention</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="DISMISSED">Dismissed</option>
            </select>

            <div className="flex min-w-72 items-center gap-3 rounded-xl border border-slate-700 bg-slate-950 px-4">
              <Search size={18} className="text-slate-500" />

              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search care or follow-up..."
                className="w-full bg-transparent py-3 text-sm text-white outline-none placeholder:text-slate-500"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <MetricCard
          title="Needs Attention"
          value={counts.open}
          subtitle="Open care items"
          icon={<ShieldAlert size={21} />}
          tone="danger"
        />

        <MetricCard
          title="In Progress"
          value={counts.inProgress}
          subtitle="Being addressed"
          icon={<Clock3 size={21} />}
          tone="warning"
        />

        <MetricCard
          title="Completed"
          value={counts.completed}
          subtitle="Resolved follow-up"
          icon={<CheckCircle2 size={21} />}
          tone="success"
        />
      </section>

      <section>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">
              Care Items ({filteredItems.length})
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Prioritized by ministry care urgency.
            </p>
          </div>
        </div>

        {filteredItems.length === 0 ? (
          <Card>
            <CardContent className="flex min-h-64 flex-col items-center justify-center text-center">
              <HeartPulse size={36} className="text-indigo-400" />

              <h3 className="mt-4 text-lg font-semibold text-white">
                No follow-up items found
              </h3>

              <p className="mt-2 max-w-md text-sm text-slate-400">
                Try adjusting your filters or search.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredItems.map((item) => (
              <CareItemCard
                key={item.id}
                item={item}
                updating={updateMutation.isPending}
                onStatusChange={(status) =>
                  status === "COMPLETED" ? setCompleting(item) : updateMutation.mutate({
                    itemId: item.id,
                    status,
                  })
                }
              />
            ))}
          </div>
        )}
      </section>

      <div className="flex items-start gap-3 rounded-2xl border border-indigo-500/20 bg-indigo-500/10 px-5 py-4 text-sm text-indigo-200">
        <Sparkles size={18} className="mt-0.5 shrink-0" />

        <p>
          Care well. MinistryIQ surfaces those who may need support, but your
          team's care makes the difference.
        </p>
      </div>
    </PageContainer>
  );
}


function CareItemCard({
  item,
  updating,
  onStatusChange,
}: {
  item: CareQueueItem;
  updating: boolean;
  onStatusChange: (status: CareQueueStatus) => void;
}) {
  const { permissions } = useEffectivePermissions();
  const profilePath = item.member_id
    ? `/members/${item.member_id}`
    : item.household_id
      ? `/households/${item.household_id}`
      : null;

  const profileLabel = item.member_id
    ? "View Member"
    : item.household_id
      ? "View Household"
      : null;

  const [observedAt] = useState(() => Date.now());
  const waitingDays = Math.max(
    0,
    Math.floor(
      (observedAt - new Date(item.created_at).getTime()) /
        (1000 * 60 * 60 * 24)
    )
  );

  return (
    <article
      className={`overflow-hidden rounded-2xl border border-slate-800 border-l-4 bg-slate-900/80 shadow-sm shadow-black/20 ${priorityBorder[item.priority]}`}
    >
      <div className="grid gap-6 p-5 xl:grid-cols-[240px_minmax(0,1fr)_190px_150px] xl:items-center">
        <div>
          <PriorityBadge priority={item.priority} />

          <h3 className="mt-3 font-semibold text-white">
            {item.title}
          </h3>
          <p className="mt-1 text-sm text-slate-300">{item.member_name || item.household_name}</p>

          <p className="mt-1 text-xs text-slate-500">
            {item.member_id
              ? "Member follow-up"
              : item.household_id
                ? "Household follow-up"
                : "Ministry follow-up"}
          </p>
        </div>

        <div className="xl:border-l xl:border-slate-800 xl:pl-6">
          {item.description && (
            <p className="text-sm font-medium leading-6 text-slate-200">
              {item.description}
            </p>
          )}

          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-400">
              MinistryIQ Recommendation
            </p>

            <p className="mt-1 text-sm leading-6 text-slate-400">
              {item.recommended_action}
            </p>
          </div>
        </div>

        <div className="xl:border-l xl:border-slate-800 xl:pl-6">
          <p className="text-xs text-slate-500">Status</p>

          <StatusBadge status={item.status} />

          <p className="mt-4 text-xs text-slate-500">Waiting</p>

          <p className="mt-1 text-sm font-semibold text-slate-200">
            {waitingDays === 0
              ? "Today"
              : `${waitingDays} day${waitingDays === 1 ? "" : "s"}`}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          {profilePath && profileLabel && canOpenPage(profilePath, permissions) && (
            <Link
              to={profilePath}
              className="rounded-xl border border-indigo-500/40 px-4 py-2.5 text-center text-sm font-medium text-indigo-300 transition hover:bg-indigo-500/10 hover:text-white"
            >
              {profileLabel}
            </Link>
          )}

          {item.status === "OPEN" && (
            <Button
              size="sm"
              disabled={updating || !permissions.has("CARE_MANAGE")}
              onClick={() => onStatusChange("IN_PROGRESS")}
            >
              Start Care
            </Button>
          )}

          {item.status === "IN_PROGRESS" && (
            <Button
              size="sm"
              disabled={updating || !permissions.has("CARE_MANAGE")}
              onClick={() => onStatusChange("COMPLETED")}
            >
              Complete
            </Button>
          )}

          {item.status === "COMPLETED" && (
            <Button
              size="sm"
              variant="secondary"
              disabled={updating || !permissions.has("CARE_MANAGE")}
              onClick={() => onStatusChange("IN_PROGRESS")}
            >
              Reopen
            </Button>
          )}
        </div>
      </div>
      <div className="px-5 pb-4"><CareHistory item={item} /></div>
    </article>
  );
}

function MetricCard({
  title,
  value,
  subtitle,
  icon,
  tone,
}: {
  title: string;
  value: number;
  subtitle: string;
  icon: React.ReactNode;
  tone: "danger" | "warning" | "success";
}) {
  const styles = {
    danger: "bg-rose-500/15 text-rose-400",
    warning: "bg-amber-500/15 text-amber-400",
    success: "bg-emerald-500/15 text-emerald-400",
  };

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-4">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-2xl ${styles[tone]}`}
          >
            {icon}
          </div>

          <div>
            <p className="text-sm text-slate-400">{title}</p>
            <p className="mt-1 text-3xl font-bold text-white">{value}</p>
            <p className="mt-1 text-xs text-slate-500">{subtitle}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FilterButton({
  active,
  label,
  count,
  tone = "default",
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  tone?: "default" | "critical" | "risk" | "watch" | "healthy";
  onClick: () => void;
}) {
  const toneStyles = {
    default: "text-slate-300",
    critical: "text-rose-400",
    risk: "text-orange-400",
    watch: "text-amber-400",
    healthy: "text-emerald-400",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition ${
        active
          ? "bg-indigo-500/15 text-indigo-300 ring-1 ring-indigo-500/30"
          : `hover:bg-slate-800 ${toneStyles[tone]}`
      }`}
    >
      {label}

      <span className="rounded-full bg-slate-800 px-2 py-0.5 text-xs text-slate-300">
        {count}
      </span>
    </button>
  );
}

const priorityBorder: Record<CareQueuePriority, string> = {
  CRITICAL: "border-l-rose-500",
  HIGH: "border-l-orange-500",
  MEDIUM: "border-l-amber-500",
  LOW: "border-l-emerald-500",
};

function PriorityBadge({
  priority,
}: {
  priority: CareQueuePriority;
}) {
  const styles: Record<CareQueuePriority, string> = {
    CRITICAL: "bg-rose-500/15 text-rose-400",
    HIGH: "bg-orange-500/15 text-orange-400",
    MEDIUM: "bg-amber-500/15 text-amber-400",
    LOW: "bg-emerald-500/15 text-emerald-400",
  };

  const labels: Record<CareQueuePriority, string> = {
    CRITICAL: "CRITICAL",
    HIGH: "AT RISK",
    MEDIUM: "WATCH",
    LOW: "HEALTHY",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${styles[priority]}`}
    >
      {labels[priority]}
    </span>
  );
}

function StatusBadge({
  status,
}: {
  status: CareQueueStatus;
}) {
  const styles: Record<CareQueueStatus, string> = {
    OPEN: "bg-rose-500/15 text-rose-400",
    IN_PROGRESS: "bg-amber-500/15 text-amber-400",
    COMPLETED: "bg-emerald-500/15 text-emerald-400",
    DISMISSED: "bg-slate-800 text-slate-400",
  };

  const labels: Record<CareQueueStatus, string> = {
    OPEN: "Needs Attention",
    IN_PROGRESS: "In Progress",
    COMPLETED: "Completed",
    DISMISSED: "Dismissed",
  };

  return (
    <span
      className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[status]}`}
    >
      {labels[status]}
    </span>
  );
}

